import { Examination, Examination_Type, Grade, Ml_Log_Status, Trial } from "@generated/graphql";
import { ExaminationItem } from "components/ExaminationList/types";
import ROUTES from "constants/routes";
import dayjs from "dayjs";
import { Maybe } from "graphql/jsutils/Maybe";

const formatNumber = (titles: string[], num: number | null | undefined, onlyTitle?: boolean) => {
  const currentNumber = num ?? 0;
  // titles example ['день', 'дня', 'дней']
  const cases = [2, 0, 1, 1, 1, 2];
  return (
    (onlyTitle ? "" : `${Intl.NumberFormat().format(currentNumber)} `) +
    titles[
      Math.abs(<number>currentNumber) % 100 > 4 && Math.abs(<number>currentNumber) % 100 < 20
        ? 2
        : cases[Math.abs(<number>currentNumber) % 10 < 5 ? Math.abs(<number>currentNumber) % 10 : 5]
    ]
  );
};

const generateOptions = (object: object) =>
  Object.entries(object).reduce((acc: object[], current: string[]) => {
    const newValue = {
      displayName: current[1],
      id: current[0],
    };
    acc.push(newValue);
    return acc;
  }, []);

const gradeColor = (grade: Grade) => {
  if (grade === Grade.Junior) return "green";
  if (grade === Grade.Middle) return "orange";
  return "red";
};

const confirmationColor = (confirmation: boolean) => {
  if (confirmation) return "green";
  return "orange";
};

const createUserName = (
  user:
    | {
        firstName?: string | null | undefined;
        lastName?: string | null | undefined;
        middleName?: string | null | undefined;
      }
    | undefined
    | null
) => {
  if (!user) return "—";
  const { firstName, lastName, middleName } = user;
  return firstName || lastName || middleName ? [firstName, lastName, middleName].join(" ") : "—";
};

const getInitials = (name: string = "", join: string = "") => {
  const nameArr = name
    .replace(/\s+/, " ")
    .split(" ")
    .slice(0, 2)
    .map((v) => v && v[0].toUpperCase());
  return nameArr.length === 0 || nameArr.length === 1 ? nameArr.join("") : nameArr.join(join);
};

const getTestingPath = (type: "trial" | "examination", id?: string) => {
  let path = `/${ROUTES.TESTING}/${id ? "view" : "create"}`;

  if (type === "trial") {
    path = `${path}/${ROUTES.PERSONAL_TESTING}`;
  } else {
    path = `${path}/${ROUTES.GROUP_TESTING}`;
  }

  if (id) {
    path = `${path}/${id}`;
  }

  return path;
};

const secondsToMinutes = (seconds: number | null | undefined) =>
  seconds ? dayjs.duration(seconds, "s").format("HH:mm:ss") : "-";

const formatPhoneNumber = (phoneNumber: string | null | undefined): string | null => {
  if (!phoneNumber) return null;
  const digits = phoneNumber.replace(/\D/g, "");
  const core = digits.replace(/^8/, "7").replace(/^7/, "");

  if (core.length !== 10) return null;

  return `+7 (${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6, 8)}-${core.slice(8)}`;
};

const toggleItemInArray = (selected: string[], id: string) => {
  const selectedIndex = selected.indexOf(id);
  const newSelected: string[] = [];

  if (selectedIndex === -1) {
    return newSelected.concat(selected, id);
  }
  if (selectedIndex === 0) {
    return newSelected.concat(selected.slice(1));
  }
  if (selectedIndex === selected.length - 1) {
    return newSelected.concat(selected.slice(0, -1));
  }

  return newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
};

const getPersonalExaminationLink = (trialId: Trial["id"], candidateLogin: Maybe<string>) => {
  return candidateLogin
    ? `${process.env.REACT_APP_ASSESSMENT_URL}/login`
    : `https://t.me/${process.env.REACT_APP_BOT_URL}?start=${trialId}`;
};

const getOpenExaminationLink = (examinationId: Examination["id"]) => {
  return `${process.env.REACT_APP_ASSESSMENT_URL}/examination/${examinationId}`;
};

const getPublicExaminationLink = (examinationId: Examination["id"]) => {
  return `${process.env.REACT_APP_ASSESSMENT_URL}/public-examination/${examinationId}`;
};

const getTestitngTypeTitle = (questionsTime: number | null | undefined) => {
  if (!questionsTime) return undefined;
  if (questionsTime <= 1800) return "Скриннинг";
  if (questionsTime > 1800 && questionsTime <= 7200) return "Ассесмент";
  return "Расширенный ассесмент";
};

const getQuestionsGenLogsStatus = (logs: { status: Ml_Log_Status }[] | undefined) => {
  if (!logs || !logs.length) return null;

  let status = Ml_Log_Status.Success;

  // eslint-disable-next-line no-restricted-syntax
  for (const log of logs) {
    if (log.status === Ml_Log_Status.Initialized) {
      status = Ml_Log_Status.Initialized;
      break;
    }

    if (log.status !== Ml_Log_Status.Success) {
      status = Ml_Log_Status.Error;
    }
  }

  return status;
};

export const getTrialLinkString = (
  examination: {
    __typename?: "Examination";
    id: string;
    type: Examination_Type;
    questionsTime?: number | null;
  },
  trial?: NonNullable<ExaminationItem["trials"]>[number]
): string | undefined => {
  switch (examination.type) {
    case Examination_Type.Personal:
    case Examination_Type.Group: {
      if (!trial) return undefined;

      return getPersonalExaminationLink(trial.id, trial.companyCandidate.candidateUsername?.login);
    }
    case Examination_Type.Open:
      return getOpenExaminationLink(examination.id);
    case Examination_Type.Public:
      return getPublicExaminationLink(examination.id);
    default:
      return undefined;
  }
};

export {
  generateOptions,
  formatNumber,
  gradeColor,
  confirmationColor,
  createUserName,
  getInitials,
  getTestingPath,
  secondsToMinutes,
  formatPhoneNumber,
  toggleItemInArray,
  getPersonalExaminationLink,
  getOpenExaminationLink,
  getPublicExaminationLink,
  getTestitngTypeTitle,
  getQuestionsGenLogsStatus,
};
